/**
 * De spelregels van Krabbelworm, los van het scherm.
 *
 * Alles hier is een pure functie op een gewoon object, zodat de regels in
 * tests/snake.test.mjs zonder browser nagelopen kunnen worden. Het tekenen en de
 * klok staan in logic/snake.js. In de code heet dit snake, want dat is het spel
 * waar de regels van komen; in beeld heet het Krabbelworm.
 *
 * Het veld is 24x24 en de worm is één pixel dik, net als een pixel op het
 * tekendoek. Een plek op het veld is één getal: y * 24 + x. Dat maakt "zit de worm
 * hier al?" een vergelijking in plaats van een zoektocht door een lijst met paren.
 *
 * ---- wat je eet gaat er zichtbaar door ------------------------------------
 *
 * Eet je een balletje, dan word je niet meteen langer. Het balletje blijft liggen
 * waar het lag en schuift als een bult door de worm heen; pas als het er aan de
 * staart uit komt, groeit hij. Een lange worm doet daar dus langer over dan een
 * korte. Zie bultOp() voor de vorm van die bult en asVan() voor de kant waar hij
 * dwars op moet staan.
 *
 * ---- verder als Snake II op de 3310 --------------------------------------
 *
 * De randen lopen door, je gaat alleen dood aan jezelf, en hoe langer je leeft
 * hoe sneller het gaat. Elke vijfde hap is een bonus die na een tijdje weer
 * verdwijnt, en elke vierde een blauw rondje dat je even twee keer zo snel maakt.
 */

export const VELD = 24;
export const VAKKEN = VELD * VELD;

/** Hoeveel vakjes je per hap langer wordt, zodra de bult eruit komt. */
export const GROEI = 3;

/** Hoeveel vakjes van de worm een bult beslaat: twee pixels lang. */
export const BULT_LANG = 2;

/** Vijf happen erbij is een niveau erbij, tot negen. */
export const HAP_PER_NIVEAU = 5;
export const MAX_NIVEAU = 9;

/** De bonus staat zestig tikken op het veld. */
export const BONUS_TIKKEN = 60;
export const BONUS_PER = 5;

/** Het blauwe rondje: even twee keer zo snel, en het geeft geen lengte. */
export const SNEL_PER = 4;
export const SNEL_TIKKEN = 60;
export const SNEL_LEVEN = 70;
export const SNEL_PUNTEN = 2;

/**
 * Hoe lang één stap duurt. Niveau 1 is 150 milliseconden, elk niveau tien
 * minder. Sneller dan 70 is op een telefoon niet meer te sturen.
 */
export function tempo(niveau) {
  return 150 - (Math.min(niveau, MAX_NIVEAU) - 1) * 10;
}

/**
 * Hoe lang één stap duurt in dit spel, nu: hetzelfde als tempo(), maar de helft
 * zolang het blauwe rondje nawerkt. De klok vraagt dit elke stap opnieuw, dus het
 * versnelt en vertraagt vanzelf.
 */
export function tempoVan(spel) {
  const ms = tempo(niveauVan(spel.happen));
  return spel.snelTot > spel.tik ? Math.round(ms / 2) : ms;
}

/** Gaat de worm nu twee keer zo snel? */
export function isSnel(spel) {
  return spel.snelTot > spel.tik;
}

export function niveauVan(happen) {
  return Math.min(MAX_NIVEAU, 1 + Math.floor(happen / HAP_PER_NIVEAU));
}

export const xVan = (cel) => cel % VELD;
export const yVan = (cel) => Math.floor(cel / VELD);
export const cel = (x, y) => y * VELD + x;

/**
 * Eén stap opzij. De rand loopt door: ga je er rechts uit, dan kom je er links
 * weer in. Dat is precies het verschil tussen Snake I en Snake II.
 */
export function verplaats(c, dx, dy) {
  const x = (xVan(c) + dx + VELD) % VELD;
  const y = (yVan(c) + dy + VELD) % VELD;
  return cel(x, y);
}

/**
 * Een vrij vakje voor de volgende hap.
 *
 * We tellen eerst hoeveel vakjes vrij zijn en pakken dan de n-de daarvan, in
 * plaats van blind te gokken tot we een leeg vakje raken. Met een worm van
 * vijfhonderd vakjes lang zou dat gokken bijna nooit meteen lukken.
 *
 * Levert -1 als het veld vol is. Dan heb je het spel uitgespeeld.
 */
export function vrijVakje(bezet, rng) {
  const vrij = [];
  for (let c = 0; c < VAKKEN; c++) if (!bezet.has(c)) vrij.push(c);
  if (!vrij.length) return -1;
  return vrij[Math.min(vrij.length - 1, Math.floor(rng() * vrij.length))];
}

/** Waar de worm en de balletjes nu liggen; alles wat niet vrij is. */
function bezetting(spel) {
  const s = new Set(spel.slang);
  if (spel.hap >= 0) s.add(spel.hap);
  if (spel.bonus) s.add(spel.bonus.cel);
  if (spel.snel) s.add(spel.snel.cel);
  return s;
}

/**
 * Een nieuw spel: drie vakjes in het midden, kop naar rechts, en één balletje.
 *
 * De worm staat er meteen als een worm bij en niet als één stip, want dan zie je
 * in de eerste seconde al welke kant hij op gaat.
 */
export function nieuwSpel(rng = Math.random) {
  const midden = cel(VELD / 2, VELD / 2);
  const spel = {
    slang: [midden, midden - 1, midden - 2],
    richting: [1, 0],
    wachtrij: [],
    hap: -1,
    bonus: null,
    snel: null,
    snelTot: 0,
    // Wat je op hebt en nog niet verteerd is: elke bult heeft zijn plek in de
    // worm en hoeveel lengte hij oplevert.
    bulten: [],
    groei: 0,
    happen: 0,
    score: 0,
    tik: 0,
    dood: false,
    vol: false
  };
  spel.hap = vrijVakje(bezetting(spel), rng);
  return spel;
}

/**
 * Een draai in de wachtrij zetten.
 *
 * Twee dingen mogen niet. Omkeren mag niet, want dan rijd je je eigen nek in;
 * dat voelt als een bug en niet als een fout van jou. En dezelfde kant als waar
 * je al heen gaat is geen draai, die slaan we over zodat de wachtrij niet
 * volloopt met niets.
 *
 * De wachtrij is er omdat één stap 70 tot 150 milliseconden duurt. Wil je snel
 * om een hoek, dan tik je twee pijltjes binnen één stap; zonder wachtrij was de
 * eerste dan weg. Twee is genoeg: meer vooruit denken dan een hoek doe je niet.
 */
export function draai(spel, dx, dy) {
  if (spel.dood) return spel;
  const [lx, ly] = spel.wachtrij.length ? spel.wachtrij[spel.wachtrij.length - 1] : spel.richting;
  if (dx === lx && dy === ly) return spel;
  if (dx === -lx && dy === -ly) return spel;
  if (spel.wachtrij.length >= 2) return spel;
  return { ...spel, wachtrij: spel.wachtrij.concat([[dx, dy]]) };
}

/**
 * Eén stap verder.
 *
 * De staart gaat er eerst af en dan komt de kop erbij, behalve als je aan het
 * groeien bent. Andersom zou het laatste staartvakje nog bezet zijn op het
 * moment dat de kop er in mag: je gaat dan dood aan een stukje worm dat er een
 * milliseconde later niet meer is.
 */
export function stap(spel, rng = Math.random) {
  if (spel.dood || spel.vol) return spel;

  const wachtrij = spel.wachtrij.slice();
  const richting = wachtrij.length ? wachtrij.shift() : spel.richting;
  const groeit = spel.groei > 0;
  const romp = groeit ? spel.slang : spel.slang.slice(0, -1);
  const kop = verplaats(spel.slang[0], richting[0], richting[1]);

  if (romp.indexOf(kop) >= 0) {
    return { ...spel, richting, wachtrij, dood: true };
  }

  const slang = [kop].concat(romp);
  const niveau = niveauVan(spel.happen);
  let { hap, bonus, snel, snelTot, groei, happen, score } = spel;
  groei = Math.max(0, groei - 1);

  /**
   * De bulten schuiven één plek naar achteren. Dat is niet de bult die beweegt:
   * de worm schuift eronderdoor. Een balletje blijft dus liggen waar het lag, en
   * je ziet het van de kop naar de staart door de worm trekken.
   */
  let bulten = spel.bulten.map((b) => ({ ...b, pos: b.pos + 1 }));
  // Wat er aan de staart uit komt, is verteerd: dat is het moment van groeien.
  bulten = bulten.filter((b) => {
    if (b.pos < slang.length) return true;
    groei += b.groei;
    return false;
  });

  const slik = (lengte) => bulten.push({ pos: 0, groei: lengte });

  if (kop === hap) {
    happen += 1;
    score += niveau;
    slik(GROEI);
    hap = -1;
    // Elke vijfde hap zet er een bonus bij, zolang er nog geen ligt.
    if (happen % HAP_PER_NIVEAU === 0 && !bonus) {
      const c = vrijVakje(new Set(slang), rng);
      if (c >= 0) bonus = { cel: c, tot: spel.tik + 1 + BONUS_TIKKEN };
    }
    // En elke vierde hap een blauw rondje, ook zolang er nog geen ligt.
    if (happen % SNEL_PER === 0 && !snel) {
      const bezetNu = new Set(slang);
      if (bonus) bezetNu.add(bonus.cel);
      const c = vrijVakje(bezetNu, rng);
      if (c >= 0) snel = { cel: c, tot: spel.tik + 1 + SNEL_LEVEN };
    }
  } else if (bonus && kop === bonus.cel) {
    score += niveau * BONUS_PER;
    slik(GROEI * 2);
    bonus = null;
  } else if (snel && kop === snel.cel) {
    score += niveau * SNEL_PUNTEN;
    snelTot = spel.tik + 1 + SNEL_TIKKEN;
    // Het rondje gaat er ook zichtbaar door, maar het maakt je niet langer.
    slik(0);
    snel = null;
  }

  const tik = spel.tik + 1;
  if (bonus && tik >= bonus.tot) bonus = null;
  if (snel && tik >= snel.tot) snel = null;

  const tussen = { ...spel, slang, richting, wachtrij, hap, bonus, snel, snelTot, bulten, groei, happen, score, tik };
  if (hap < 0) {
    const c = vrijVakje(bezetting(tussen), rng);
    // Geen vrij vakje meer: het hele veld is worm. Dat is winst, niet dood.
    if (c < 0) return { ...tussen, vol: true };
    tussen.hap = c;
  }
  return tussen;
}

/**
 * Zit er een bult op vakje `i` van de worm?
 *
 * Een bult is twee vakjes lang: hij begint op zijn eigen plek en loopt één vakje
 * verder naar de staart. Het tekenwerk zet daar een pixel aan beide zijden bij,
 * dwars op de worm.
 */
export function bultOp(spel, i) {
  return spel.bulten.some((b) => i >= b.pos && i < b.pos + BULT_LANG);
}

/** Eén stap van a naar b, ook als die stap door de rand heen gaat. */
function tussenVakjes(a, b) {
  const wikkel = (d) => (d === VELD - 1 ? -1 : d === -(VELD - 1) ? 1 : d);
  return [wikkel(xVan(b) - xVan(a)), wikkel(yVan(b) - yVan(a))];
}

/**
 * Welke kant loopt de worm op bij vakje `i`.
 *
 * Het tekenwerk heeft dit nodig om een bult dwars op de worm te zetten: loopt hij
 * daar horizontaal, dan komt de verdikking boven en onder, en loopt hij verticaal
 * dan links en rechts.
 *
 * Voor de kop is dat de richting waar hij heen gaat. Voor de rest is het de stap
 * van dit vakje naar het vakje ervoor, dus naar de kop toe. In een bocht kiest hij
 * daarmee de kant waar de worm dit vakje uit gaat, en dat is de kant die je als
 * kijker als "vooruit" leest.
 */
export function asVan(spel, i) {
  if (i <= 0 || spel.slang[i - 1] === undefined) return spel.richting;
  return tussenVakjes(spel.slang[i], spel.slang[i - 1]);
}
